import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { uploadToS3 } from "utils/aws/uploadToS3";
import { useUploadCustomerDocument } from "hooks/useUploadCustomerDocument";
import useANUploader from "hooks/utils/useANUploader";
import { ANUploader, ResponsiveModal } from "@answersnow/pkg-theme";
import * as selectors from "selectors";
import { Task } from "api/types/users";
import { getTaskDisplayName } from "../utils/getTaskDisplayName";

interface DiagnosisFormUploadProps {
  task: Task;
  onExited: () => void;
  onUploadSuccess?: () => void;
}

const DiagnosisFormUpload: React.FC<DiagnosisFormUploadProps> = ({
  task,
  onExited,
  onUploadSuccess,
}) => {
  const { t, i18n } = useTranslation(["dashboard"]);
  const uploaderLanguage = i18n.language?.startsWith("es") ? "es" : "en";
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  const { handleFileUpload, dropzoneFiles, handleFileDelete } = useANUploader(false) as {
    handleFileUpload: (file: any) => void;
    dropzoneFiles: any[];
    handleFileDelete: (file: any) => void;
  };

  const clientId = useSelector(selectors.getClientId);
  const userId = useSelector(selectors.getUserId);
  const userCognitoId = useSelector(selectors.getUserCognitoId);

  const { uploadDocument } = useUploadCustomerDocument();

  // Extract document type from task metadata
  const documentType = task.metadata?.documentType || "DIAGNOSIS_FORM";

  const handleUpload = async () => {
    const selectedFile = dropzoneFiles[0]?.file;
    if (!selectedFile) return;
    setUploadError(null);

    try {
      setIsUploading(true);

      const file = selectedFile;
      const fileUUID = uuidv4();
      const [fileName, fileExtension] = file.name.split(".");
      const key = `${userCognitoId}/${fileName}-${fileUUID}.${fileExtension}`;

      // 1) Upload to S3
      const upload = await uploadToS3({ file, key });

      // 2) Save metadata via React Query mutation
      await uploadDocument({
        filename: (upload.Key as string).split("/")[1],
        key: upload.Key as string,
        display_filename: file.name,
        clientId: clientId,
        type: documentType,
        uploaded_by: userId,
        file_size: file.size,
      });

      // Close modal on success; parent will handle marking task complete
      if (onUploadSuccess) {
        await onUploadSuccess();
      }

      setOpen(false);
    } catch (error) {
      console.error(error);
      setUploadError(t("dashboard:parent.myTasks.imageUpload.uploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      onOpen={() => setOpen(true)}
      onTransitionExited={onExited}
      title={getTaskDisplayName(task, t)}
      maxWidth="xs"
    >
      <Box sx={{ py: 2 }}>
        <ANUploader
          handleFileUpload={handleFileUpload}
          dropzoneFiles={dropzoneFiles}
          handleFileDelete={handleFileDelete}
          handleLoadedFileDelete={() => {}}
          onViewFile={() => {}}
          alreadyUploadedFiles={[]}
          error={false}
          errorText={""}
          language={uploaderLanguage}
          useDropzoneOptions={{
            multiple: false,
            accept: [
              "image/*",
              ".pdf",
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              "application/rtf",
              "text/plain",
            ],
          }}
          dropZoneStyle={{ margin: 0 }}
          filePreviewStyle={{ margin: 0 }}
          hideDropzone={dropzoneFiles.length > 0}
          maxFileSize={undefined}
        />

        {uploadError && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {uploadError}
          </Typography>
        )}

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <LoadingButton
            onClick={handleUpload}
            variant="contained"
            disabled={!dropzoneFiles.length || dropzoneFiles[0]?.errors}
            loading={isUploading}
            fullWidth
          >
            {t("dashboard:parent.myTasks.imageUpload.uploadButton")}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default DiagnosisFormUpload;
